import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import FilterBtn from "../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import CustomLoadingOverlay from "../../../Components/CustomLoadingOverlay";
import { createFilterHandler } from "../../../Utils/FilterUtils";
import httpclient from "../../../Utils";

import { getColumns } from "./columns";
import Filter from "./Filter";
import {
  useCustomNotification,
  fieldMappings,
  rangeFields,
  valueTransformers,
} from "./useCustomNotification";
import ConfirmationDialog from "./ConfirmationDialog";
import SendNotificationConfirmationModal from "./SendNotificationConfirmationModal";
import ViewUsersModal from "./ViewUsersModal";

const INITIAL_FILTER_STATE = {
  isNotified: "",
  notifiedDate: null,
  notifiedDateStart: null,
  notifiedDateEnd: null,
};

export default function CustomNotification() {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [openDeleteNotification, setOpenDeleteNotification] = useState(false);
  const [openSendNotificationModal, setOpenSendNotificationModal] =
    useState(false);
  const [openViewUsersModal, setOpenViewUsersModal] = useState(false);
  const [selectedNotificationForUsers, setSelectedNotificationForUsers] =
    useState(null);
  const [isDeletingNotification, setIsDeletingNotification] = useState(false);
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([]);

  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const {
    notifications,
    setNotifications,
    loading,
    setLoading,
    rowCount,
    refresh,
    refreshNotifications,
    allowAdd,
    submittedData,
    setSubmittedData,
  } = useCustomNotification({
    autoFetch: true,
    pagination: {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    sorting: sortModel,
  });

  const handleChangeFilter = (field) => (event, value) => {
    if (
      field.includes("Date") ||
      field.includes("Start") ||
      field.includes("End")
    ) {
      setFilterData((prev) => ({
        ...prev,
        [field]: value || (event && event.target.value) || null,
      }));
      return;
    }

    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setNotifications,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `/custom-notifications`,
    fieldMappings,
    rangeFields,
    valueTransformers,
    refresh,
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    newFilterData[field] =
      field.includes("Start") || field.includes("End") ? null : "";
    delete newSubmittedData[field];
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleAddNotification = () => {
    navigate("/custom-notifications/add");
  };

  const handleDeleteNotification = async () => {
    setIsDeletingNotification(true);

    const API_URL = `/custom-notifications/${selectedNotification.id}`;

    try {
      const { data } = await httpclient.delete(API_URL);
      if (data.status === 200) {
        showNotification("success", data.message);
        setSelectedNotification(null);
        setOpenDeleteNotification(false);
        refreshNotifications();
      } else {
        showNotification("info", data.message);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setTimeout(() => {
          setOpenDeleteNotification(false);
        }, 1000);
      } else if (error.response?.status === 401) {
        refresh();
      }
      handleApiError(error);
    } finally {
      setIsDeletingNotification(false);
    }
  };

  const handleSendNotification = async () => {
    setIsSendingNotification(true);
    const API_URL = `/custom-notifications/send/${selectedRow.id}`;

    try {
      const { data } = await httpclient.post(API_URL);

      if (data.status === 200) {
        showNotification("success", data.message);
        setSelectedRow(null);
        setOpenSendNotificationModal(false);
        refreshNotifications();
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      console.log(error, "error sending notification");
      handleApiError(error);
    } finally {
      setIsSendingNotification(false);
    }
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = getColumns(
    setSelectedNotification,
    setOpenDeleteNotification,
    setSelectedRow,
    setOpenSendNotificationModal,
    setOpenViewUsersModal,
    setSelectedNotificationForUsers
  );

  const deleteNotificationMessage = (
    <>
      Are you sure you want to delete notification:{" "}
      <strong>{selectedNotification?.message}</strong>?
    </>
  );

  const sendNotificationMessage = (
    <>
      Are you sure you want to send the notification:{" "}
      <strong>{selectedRow?.message}</strong> now?
    </>
  );

  return (
    <>
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 16px",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Custom Notification
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {allowAdd === 1 && (
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: "4px",
                    marginRight: "16px",
                    backgroundColor: "#7152F3",
                    "&:hover": {
                      backgroundColor: "#5c34fa",
                    },
                  }}
                  startIcon={<AddIcon sx={{ marginRight: 0 }} />}
                  onClick={handleAddNotification}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "none",
                    }}
                  >
                    Add Notification
                  </Typography>
                </Button>
              )}
              <img
                src={filterOpen ? FilterBtnUp : FilterBtn}
                alt="Filter"
                style={{ cursor: "pointer" }}
                onClick={() => setFilterOpen(!filterOpen)}
              />
            </Box>
          </Box>

          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
          />

          <DataGrid
            rows={notifications || []}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            slots={{
              loadingOverlay: CustomLoadingOverlay,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#48505E",
              },
            }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
          />
        </Box>
      </Box>

      {/* Delete confirmation dialog */}
      {openDeleteNotification && (
        <ConfirmationDialog
          open={openDeleteNotification}
          handleClose={() => setOpenDeleteNotification(false)}
          title={"Delete Notification"}
          message={deleteNotificationMessage}
          selectedNotification={selectedNotification}
          onConfirm={handleDeleteNotification}
          loading={isDeletingNotification}
        />
      )}

      {/* Send notification confirmation dialog */}
      {openSendNotificationModal && (
        <SendNotificationConfirmationModal
          open={openSendNotificationModal}
          cancel={() => {
            setOpenSendNotificationModal(false);
            setSelectedRow(null);
          }}
          title="Send Notification"
          message={sendNotificationMessage}
          loading={isSendingNotification}
          handleSendNotification={handleSendNotification}
        />
      )}

      {/* View Users Modal */}
      {openViewUsersModal && (
        <ViewUsersModal
          open={openViewUsersModal}
          onClose={() => {
            setOpenViewUsersModal(false);
            setSelectedNotificationForUsers(null);
          }}
          notification={selectedNotificationForUsers}
        />
      )}
    </>
  );
}
